import { useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { posthog } from 'posthog-js';

import { PERSONAL_EXPENSES_KEY } from './constants';
import { patchPersonalExpense } from './personalExpenses';
import type { RegisterExpenseFormData } from '@/pages/register/expenses';
import { parseLocaleNumber } from '@/utils';

export const useUpdatePersonalExpense = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      expenseData,
    }: {
      id: number;
      expenseData: RegisterExpenseFormData;
    }) => {
      const mappedPayload = {
        projectId: Number(expenseData.project_information.project?.id),
        projectSubContractId: expenseData.project_information.contract?.id
          ? Number(expenseData.project_information.contract?.id)
          : null,
        expenseTypeId: Number(expenseData.expense_details.expenseType?.id),
        supplierId: expenseData.expense_details.supplier?.id
          ? Number(expenseData.expense_details.supplier?.id)
          : null,
        supplierInvoiceNo: expenseData.expense_details.supplierInvoiceNumber
          ? expenseData.expense_details.supplierInvoiceNumber
          : null,
        expenseNo: expenseData.expense_details.expenseNumber
          ? expenseData.expense_details.expenseNumber
          : null,
        date: format(expenseData.expense_details.date, 'yyyy-MM-dd'),
        amountIncludingVat: parseLocaleNumber(
          expenseData.expense_details.amountIncludingVat,
        ),
        vatAmount: parseLocaleNumber(expenseData.expense_details.vatAmount),
        paymentMethodId: Number(expenseData.expense_details.paymentMethod?.id),
        comment: expenseData.expense_details.expenseComment || '',
        isBillable: expenseData.expense_details.billable || false,
        userId: Number(expenseData.project_information.employee?.id),
        currencyCode:
          (expenseData.expense_details.expenseCurrency?.isoCode as string) ??
          '',
        exchangeRate: expenseData.exchangeRate,
        baseReferenceCurrencyCode: expenseData.legalEntityCurrencyIsoCode ?? '',
      };
      return patchPersonalExpense(id, mappedPayload);
    },
    onSuccess: (updatedPersonalExpense) => {
      console.debug('Personal expense updated:', updatedPersonalExpense);
      queryClient.invalidateQueries({
        queryKey: [PERSONAL_EXPENSES_KEY, updatedPersonalExpense.expenseId],
      });
      posthog.capture('personal_expense_updated');
    },
  });
};
